import { useState } from 'react'
import { LabResult } from '../types/labResults'

interface PublicLabVerificationProps {
  compact?: boolean
}

export default function PublicLabVerification({ compact = false }: PublicLabVerificationProps) {
  const [verificationCode, setVerificationCode] = useState('')
  const [batchNumber, setBatchNumber] = useState('')
  const [verificationResult, setVerificationResult] = useState<LabResult | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const [error, setError] = useState('')

  // Mock verification function
  const handleVerification = async () => {
    setIsVerifying(true)
    setError('')
    
    // Simulate API call
    setTimeout(() => {
      if (verificationCode === 'verify:TH7OH-240115-001:abc123' || batchNumber === 'TH7OH-240115-001') {
        // Mock successful verification
        setVerificationResult({
          id: 'lab-result-1',
          productId: 'thai-7oh-powder-1',
          vendorId: 'vendor-1',
          batchNumber: 'TH7OH-240115-001',
          testDate: new Date('2024-01-15'),
          labName: 'ACS Laboratory',
          labCertificationNumber: 'DEA-123456',
          labContactInfo: {
            phone: '(555) 123-4567',
            email: 'info@acslaboratory.com',
            website: 'https://acslaboratory.com',
            address: '123 Lab Street, Science City, SC 12345'
          },
          alkaloids: {
            mitragynine: 1.2,
            '7ohMitragynine': 6.8,
            paynantheine: 0.3,
            speciogynine: 0.2,
            total: 8.5
          },
          safetyTests: {
            heavyMetals: {
              lead: 0.1,
              mercury: 0.05,
              cadmium: 0.02,
              arsenic: 0.08,
              passed: true
            },
            microbials: {
              salmonella: 'not-detected',
              ecoli: 'not-detected',
              yeastMold: 50,
              totalBacteria: 100,
              passed: true
            },
            pesticides: {
              tested: true,
              detected: [],
              passed: true
            },
            residualSolvents: {
              tested: true,
              detected: [],
              passed: true
            }
          },
          verification: {
            status: 'verified',
            verifiedBy: 'admin-1',
            verifiedDate: new Date('2024-01-16'),
            expiryDate: new Date('2024-07-15')
          },
          certificate: {
            originalFileName: 'TH7OH-240115-001-COA.pdf',
            fileUrl: '/certificates/TH7OH-240115-001-COA.pdf',
            uploadDate: new Date('2024-01-15'),
            fileHash: 'sha256:abc123...',
            qrCodeData: 'verify:TH7OH-240115-001:abc123'
          },
          testingMethod: 'HPLC-UV',
          sampleWeight: 5.0,
          moistureContent: 8.2,
          isPublic: true,
          createdDate: new Date('2024-01-15'),
          lastUpdated: new Date('2024-01-16')
        })
      } else {
        setError('Verification code or batch number not found. Please check your input and try again.')
      }
      setIsVerifying(false)
    }, 1500)
  }

  const handleReset = () => {
    setVerificationCode('')
    setBatchNumber('')
    setVerificationResult(null)
    setError('')
  }

  const isValidResult = verificationResult && 
    verificationResult.verification.status === 'verified' &&
    new Date() < verificationResult.verification.expiryDate!

  const allTestsPassed = verificationResult?.safetyTests.heavyMetals.passed && 
                        verificationResult?.safetyTests.microbials.passed && 
                        verificationResult?.safetyTests.pesticides.passed && 
                        verificationResult?.safetyTests.residualSolvents.passed

  if (compact) {
    return (
      <div className="compact-verification">
        <div className="compact-header">
          <h4>🔬 Verify Lab Results</h4>
        </div>
        <div className="compact-form">
          <input
            type="text"
            placeholder="Enter verification code or batch number"
            value={verificationCode || batchNumber}
            onChange={(e) => {
              if (e.target.value.startsWith('verify:')) {
                setVerificationCode(e.target.value)
                setBatchNumber('')
              } else {
                setBatchNumber(e.target.value)
                setVerificationCode('')
              }
            }}
          />
          <button onClick={handleVerification} disabled={isVerifying}>
            {isVerifying ? '⏳' : '🔍'}
          </button>
        </div>

        <style>{`
          .compact-verification {
            background: #f8fdf8;
            border: 1px solid #e8f5e8;
            border-radius: 8px;
            padding: 15px;
            margin: 15px 0;
          }

          .compact-header h4 {
            color: #2c5530;
            margin: 0 0 10px 0;
            font-size: 14px;
          }

          .compact-form {
            display: flex;
            gap: 10px;
          }

          .compact-form input {
            flex: 1;
            padding: 8px;
            border: 1px solid #4a5568;
            border-radius: 4px;
            font-size: 12px;
          }

          .compact-form button {
            padding: 8px 12px;
            background: #2c5530;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
          }

          .compact-form button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="public-lab-verification">
      <div className="verification-header">
        <h2>🔬 Lab Result Verification</h2>
        <p>Verify the authenticity and validity of lab test results for kratom products</p>
      </div>

      {!verificationResult ? (
        <div className="verification-form">
          <div className="form-section">
            <h3>How to Verify Lab Results</h3>
            <div className="instructions">
              <div className="instruction-item">
                <span className="instruction-number">1</span>
                <div className="instruction-content">
                  <h4>Find Your Verification Information</h4>
                  <p>Look for a QR code or verification code on your Certificate of Analysis (COA)</p>
                </div>
              </div>
              <div className="instruction-item">
                <span className="instruction-number">2</span>
                <div className="instruction-content">
                  <h4>Enter Verification Details</h4>
                  <p>Scan the QR code or enter the verification code/batch number below</p>
                </div>
              </div>
              <div className="instruction-item">
                <span className="instruction-number">3</span>
                <div className="instruction-content">
                  <h4>View Verified Results</h4>
                  <p>See the complete lab analysis with safety and potency information</p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Verify Lab Results</h3>
            <div className="verification-methods">
              <div className="method-option">
                <h4>Method 1: Verification Code</h4>
                <p>Enter the verification code from your COA (starts with "verify:")</p>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="verify:batch-number:hash"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                  />
                </div>
              </div>

              <div className="method-divider">
                <span>OR</span>
              </div>

              <div className="method-option">
                <h4>Method 2: Batch Number</h4>
                <p>Enter the batch number from your product or COA</p>
                <div className="input-group">
                  <input
                    type="text"
                    placeholder="e.g., TH7OH-240115-001"
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                {error}
              </div>
            )}

            <div className="form-actions">
              <button 
                className="verify-btn"
                onClick={handleVerification}
                disabled={isVerifying || (!verificationCode && !batchNumber)}
              >
                {isVerifying ? (
                  <>
                    <span className="loading-spinner">⏳</span>
                    Verifying...
                  </>
                ) : (
                  <>
                    <span className="verify-icon">🔍</span>
                    Verify Results
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="demo-section">
            <h3>Try the Demo</h3>
            <p>Test the verification system with these sample codes:</p>
            <div className="demo-codes">
              <div className="demo-code">
                <strong>Verification Code:</strong>
                <code>verify:TH7OH-240115-001:abc123</code>
                <button onClick={() => setVerificationCode('verify:TH7OH-240115-001:abc123')}>
                  Use This Code
                </button>
              </div>
              <div className="demo-code">
                <strong>Batch Number:</strong>
                <code>TH7OH-240115-001</code>
                <button onClick={() => setBatchNumber('TH7OH-240115-001')}>
                  Use This Batch
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="verification-results">
          <div className="results-header">
            <div className="verification-status">
              {isValidResult ? (
                <div className="status-valid">
                  <span className="status-icon">✅</span>
                  <div className="status-text">
                    <h3>Verification Successful</h3>
                    <p>This lab result is authentic and valid</p>
                  </div>
                </div>
              ) : (
                <div className="status-invalid">
                  <span className="status-icon">❌</span>
                  <div className="status-text">
                    <h3>Verification Failed</h3>
                    <p>This lab result could not be verified or has expired</p>
                  </div>
                </div>
              )}
            </div>
            <button className="new-verification-btn" onClick={handleReset}>
              Verify Another Result
            </button>
          </div>

          {isValidResult && (
            <div className="lab-result-display">
              
              {/* Basic Information */}
              <div className="result-section">
                <h4>Test Information</h4>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Batch Number:</span>
                    <span className="info-value">{verificationResult.batchNumber}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Test Date:</span>
                    <span className="info-value">{verificationResult.testDate.toLocaleDateString()}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Laboratory:</span>
                    <span className="info-value">{verificationResult.labName}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Testing Method:</span>
                    <span className="info-value">{verificationResult.testingMethod}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Sample Weight:</span>
                    <span className="info-value">{verificationResult.sampleWeight}g</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Moisture Content:</span>
                    <span className="info-value">{verificationResult.moistureContent}%</span>
                  </div>
                </div>
              </div>

              {/* Alkaloid Content */}
              <div className="result-section">
                <h4>Alkaloid Analysis</h4>
                <div className="alkaloid-results">
                  <div className="alkaloid-item major">
                    <span className="alkaloid-name">7-OH Mitragynine</span>
                    <span className="alkaloid-percentage">{verificationResult.alkaloids['7ohMitragynine']}%</span>
                  </div>
                  <div className="alkaloid-item major">
                    <span className="alkaloid-name">Mitragynine</span>
                    <span className="alkaloid-percentage">{verificationResult.alkaloids.mitragynine}%</span>
                  </div>
                  <div className="alkaloid-item">
                    <span className="alkaloid-name">Paynantheine</span>
                    <span className="alkaloid-percentage">{verificationResult.alkaloids.paynantheine}%</span>
                  </div>
                  <div className="alkaloid-item">
                    <span className="alkaloid-name">Speciogynine</span>
                    <span className="alkaloid-percentage">{verificationResult.alkaloids.speciogynine}%</span>
                  </div>
                  <div className="alkaloid-item total">
                    <span className="alkaloid-name">Total Alkaloids</span>
                    <span className="alkaloid-percentage">{verificationResult.alkaloids.total}%</span>
                  </div>
                </div>
              </div>

              {/* Safety Tests */}
              <div className="result-section">
                <h4>Safety Analysis</h4>
                
                <div className="safety-category">
                  <h5>Heavy Metals (ppm)</h5>
                  <div className="safety-tests">
                    <div className="safety-test">
                      <span className="test-name">Lead (Pb)</span>
                      <span className="test-value">{verificationResult.safetyTests.heavyMetals.lead}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                    <div className="safety-test">
                      <span className="test-name">Mercury (Hg)</span>
                      <span className="test-value">{verificationResult.safetyTests.heavyMetals.mercury}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                    <div className="safety-test">
                      <span className="test-name">Cadmium (Cd)</span>
                      <span className="test-value">{verificationResult.safetyTests.heavyMetals.cadmium}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                    <div className="safety-test">
                      <span className="test-name">Arsenic (As)</span>
                      <span className="test-value">{verificationResult.safetyTests.heavyMetals.arsenic}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                  </div>
                </div>

                <div className="safety-category">
                  <h5>Microbial Contamination</h5>
                  <div className="safety-tests">
                    <div className="safety-test">
                      <span className="test-name">Salmonella</span>
                      <span className="test-value">{verificationResult.safetyTests.microbials.salmonella}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                    <div className="safety-test">
                      <span className="test-name">E. Coli</span>
                      <span className="test-value">{verificationResult.safetyTests.microbials.ecoli}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                    <div className="safety-test">
                      <span className="test-name">Yeast & Mold (CFU/g)</span>
                      <span className="test-value">{verificationResult.safetyTests.microbials.yeastMold}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                    <div className="safety-test">
                      <span className="test-name">Total Bacteria (CFU/g)</span>
                      <span className="test-value">{verificationResult.safetyTests.microbials.totalBacteria}</span>
                      <span className="test-status pass">✓</span>
                    </div>
                  </div>
                </div>

                <div className="safety-summary">
                  <div className={`safety-overall ${allTestsPassed ? 'passed' : 'failed'}`}>
                    <span className="safety-icon">
                      {allTestsPassed ? '✅' : '❌'}
                    </span>
                    <span className="safety-text">
                      {allTestsPassed ? 'All Safety Tests Passed' : 'Safety Issues Detected'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Laboratory Information */}
              <div className="result-section">
                <h4>Laboratory Information</h4>
                <div className="lab-info">
                  <div className="lab-detail">
                    <strong>Laboratory:</strong> {verificationResult.labName}
                  </div>
                  <div className="lab-detail">
                    <strong>Certification:</strong> {verificationResult.labCertificationNumber}
                  </div>
                  {verificationResult.labContactInfo && (
                    <>
                      <div className="lab-detail">
                        <strong>Website:</strong>
                        <a href={verificationResult.labContactInfo.website} target="_blank" rel="noopener noreferrer">
                          {verificationResult.labContactInfo.website}
                        </a>
                      </div>
                      <div className="lab-detail">
                        <strong>Phone:</strong> {verificationResult.labContactInfo.phone}
                      </div>
                      <div className="lab-detail">
                        <strong>Address:</strong> {verificationResult.labContactInfo.address}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="result-actions">
                <button className="download-btn">
                  📄 Download Full COA
                </button>
                <button className="share-btn">
                  📤 Share Verification
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        .public-lab-verification {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: #f5f7fa;
          min-height: 100vh;
        }

        .verification-header {
          background: #2d3748;
          border-radius: 12px;
          padding: 40px;
          text-align: center;
          margin-bottom: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }

        .verification-header h2 {
          color: #2c5530;
          margin-bottom: 15px;
          font-size: 2.2em;
        }

        .verification-header p {
          color: #a0aec0;
          font-size: 1.1em;
          line-height: 1.6;
        }

        .verification-form {
          background: #2d3748;
          border-radius: 12px;
          padding: 30px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
        }

        .form-section {
          margin-bottom: 40px;
        }

        .form-section h3 {
          color: #2c5530;
          margin-bottom: 20px;
          font-size: 1.5em;
        }

        .instructions {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .instruction-item {
          display: flex;
          align-items: flex-start;
          gap: 15px;
        }

        .instruction-number {
          background: #2c5530;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          flex-shrink: 0;
        }

        .instruction-content h4 {
          color: #2c5530;
          margin-bottom: 5px;
        }

        .instruction-content p {
          color: #a0aec0;
          margin: 0;
        }

        .verification-methods {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .method-option {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 20px;
        }

        .method-option h4 {
          color: #2c5530;
          margin-bottom: 5px;
        }

        .method-option p {
          color: #a0aec0;
          margin-bottom: 15px;
          font-size: 14px;
        }

        .input-group input {
          width: 100%;
          padding: 12px;
          border: 1px solid #4a5568;
          border-radius: 6px;
          font-size: 16px;
        }

        .input-group input:focus {
          outline: none;
          border-color: #2c5530;
        }

        .method-divider {
          text-align: center;
          position: relative;
          margin: 20px 0;
        }

        .method-divider::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 0;
          right: 0;
          height: 1px;
          background: #e0e0e0;
        }

        .method-divider span {
          background: #2d3748;
          padding: 0 15px;
          color: #a0aec0;
          font-weight: 600;
          position: relative;
        }

        .error-message {
          background: #ffebee;
          border: 1px solid #ffcdd2;
          border-radius: 6px;
          padding: 12px;
          margin: 15px 0;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #c62828;
        }

        .form-actions {
          text-align: center;
        }

        .verify-btn {
          background: #2c5530;
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;
        }

        .verify-btn:hover:not(:disabled) {
          background: #1e3a21;
          transform: translateY(-2px);
        }

        .verify-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
          transform: none;
        }

        .demo-section {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 20px;
          margin-top: 30px;
        }

        .demo-section h3 {
          color: #2c5530;
          margin-bottom: 10px;
        }

        .demo-section p {
          color: #a0aec0;
          margin-bottom: 15px;
        }

        .demo-codes {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .demo-code {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 10px;
          background: #2d3748;
          border-radius: 6px;
          border: 1px solid #e0e0e0;
        }

        .demo-code strong {
          color: #e2e8f0;
          min-width: 120px;
        }

        .demo-code code {
          background: #f8f9fa;
          padding: 4px 8px;
          border-radius: 4px;
          font-family: monospace;
          flex: 1;
          font-size: 12px;
        }

        .demo-code button {
          background: #2c5530;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        }

        .verification-results {
          background: #2d3748;
          border-radius: 12px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05);
          overflow: hidden;
        }

        .results-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 30px;
          border-bottom: 1px solid #e0e0e0;
        }

        .verification-status {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .status-icon {
          font-size: 2em;
        }

        .status-text h3 {
          margin: 0 0 5px 0;
          color: #2c5530;
        }

        .status-text p {
          margin: 0;
          color: #a0aec0;
        }

        .status-invalid .status-text h3 {
          color: #c62828;
        }

        .new-verification-btn {
          background: #f5f5f5;
          color: #a0aec0;
          border: 1px solid #4a5568;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
        }

        .new-verification-btn:hover {
          background: #e8e8e8;
        }

        .lab-result-display {
          padding: 30px;
        }

        .result-section {
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 1px solid #f0f0f0;
        }

        .result-section:last-child {
          border-bottom: none;
        }

        .result-section h4 {
          color: #2c5530;
          margin-bottom: 15px;
          font-size: 1.3em;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 15px;
        }

        .info-item {
          display: flex;
          justify-content: space-between;
          padding: 10px;
          background: #f8f9fa;
          border-radius: 6px;
        }

        .info-label {
          font-weight: 600;
          color: #e2e8f0;
        }

        .info-value {
          color: #a0aec0;
        }

        .alkaloid-results {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .alkaloid-item {
          background: #f8fdf8;
          border: 1px solid #e8f5e8;
          border-radius: 8px;
          padding: 15px;
          text-align: center;
        }

        .alkaloid-item.major {
          background: #e8f5e8;
          border-color: #2c5530;
        }

        .alkaloid-item.total {
          background: #2c5530;
          color: white;
          border-color: #2c5530;
        }

        .alkaloid-name {
          display: block;
          font-weight: 600;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .alkaloid-percentage {
          display: block;
          font-size: 1.8em;
          font-weight: bold;
          color: #2c5530;
        }

        .alkaloid-item.total .alkaloid-percentage {
          color: white;
        }

        .safety-category {
          margin-bottom: 25px;
        }

        .safety-category h5 {
          color: #2c5530;
          margin-bottom: 10px;
          font-size: 1.1em;
        }

        .safety-tests {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 10px;
        }

        .safety-test {
          display: grid;
          grid-template-columns: 1fr auto auto;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 6px;
          font-size: 14px;
        }

        .test-name {
          font-weight: 500;
          color: #e2e8f0;
        }

        .test-value {
          color: #a0aec0;
          font-family: monospace;
        }

        .test-status.pass {
          color: #4caf50;
          font-weight: bold;
        }

        .safety-summary {
          margin-top: 20px;
          text-align: center;
        }

        .safety-overall {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 15px 25px;
          border-radius: 8px;
          font-weight: 600;
        }

        .safety-overall.passed {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .safety-overall.failed {
          background: #ffebee;
          color: #c62828;
        }

        .lab-info {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .lab-detail {
          padding: 8px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .lab-detail:last-child {
          border-bottom: none;
        }

        .lab-detail strong {
          color: #e2e8f0;
          margin-right: 10px;
        }

        .lab-detail a {
          color: #2c5530;
          text-decoration: none;
        }

        .lab-detail a:hover {
          text-decoration: underline;
        }

        .result-actions {
          display: flex;
          gap: 15px;
          justify-content: center;
          margin-top: 30px;
        }

        .download-btn,
        .share-btn {
          padding: 12px 20px;
          border: 1px solid #2c5530;
          border-radius: 6px;
          background: #2d3748;
          color: #2c5530;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .download-btn:hover,
        .share-btn:hover {
          background: #2c5530;
          color: white;
        }

        @media (max-width: 768px) {
          .results-header {
            flex-direction: column;
            gap: 20px;
            align-items: stretch;
          }

          .verification-status {
            justify-content: center;
          }

          .info-grid {
            grid-template-columns: 1fr;
          }

          .alkaloid-results {
            grid-template-columns: repeat(2, 1fr);
          }

          .safety-tests {
            grid-template-columns: 1fr;
          }

          .result-actions {
            flex-direction: column;
          }

          .demo-code {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }

          .demo-code strong {
            min-width: auto;
          }
        }
      `}</style>
    </div>
  )
}